import React from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { OpenInNew as ExternalIcon } from '@mui/icons-material';
import { useDocumentTitle } from '../../hooks/core';

export const BlogAdminPage: React.FC = () => {
  useDocumentTitle('Blog Admin');

  return (
    <Box sx={{ py: 4, px: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Blog Admin
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage blog posts using the Sveltia CMS editor.
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        The CMS requires GitHub authentication to be configured. For now, you can edit blog posts
        directly in the repository at <code>apps/squareup/frontend/content/blog/</code>.
      </Alert>

      <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Open CMS Editor
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click the button below to open the Sveltia CMS editor in a new window. Note: GitHub
          authentication must be configured for the editor to work properly.
        </Typography>
        <Button
          variant="contained"
          endIcon={<ExternalIcon />}
          onClick={() => window.open('/blog-admin/', '_blank')}
        >
          Open CMS Editor
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider', mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Editing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can also create and edit blog posts by adding Markdown files directly to the
          repository:
        </Typography>
        <Box
          component="ol"
          sx={{
            pl: 2,
            '& li': { mb: 1, color: 'text.secondary' },
          }}
        >
          <li>
            Create a new <code>.md</code> file in{' '}
            <code>apps/squareup/frontend/content/blog/</code>
          </li>
          <li>
            Use the filename format: <code>YYYY-MM-DD-slug.md</code>
          </li>
          <li>Add frontmatter at the top with title, date, description, category, and tags</li>
          <li>Write your content in Markdown below the frontmatter</li>
          <li>Commit and push your changes</li>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Example Frontmatter:
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: 'grey.100',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem',
          }}
        >
          {`---
title: "Your Blog Post Title"
slug: your-post-slug
date: 2025-01-01T12:00:00Z
description: "A brief description of your post"
author: "SquareUp Team"
category: "tips-strategies"
tags:
  - super bowl
  - squares
published: true
---

Your markdown content here...`}
        </Box>
      </Paper>
    </Box>
  );
};

export default BlogAdminPage;
